import { useCallback, useLayoutEffect, useMemo, useState, type ReactNode } from "react";

import ConsentBanner from "@/components/consent/ConsentBanner";
import ConsentModal from "@/components/consent/ConsentModal";
import { ConsentContext, type ConsentContextValue } from "@/contexts/consent-context";
import { captureProductEvent, configureProductAnalyticsConsent } from "@/lib/analytics";
import {
  buildAllConsentCategories,
  buildEssentialConsentCategories,
  readConsentData,
  writeConsentData,
  type ConsentCategories,
  type ConsentCategory,
  type ConsentData,
} from "@/lib/consent.utils";
import { syncMarketingAttribution } from "@/lib/marketing-consent";
import { configureClientMonitoringConsent } from "@/lib/monitoring";

function captureConsentUpdate(
  nextConsent: ConsentData,
  previousConsent: ConsentData | null,
  nextAnalyticsAllowed: boolean,
) {
  const previousAnalyticsAllowed = previousConsent?.categories.analytics === true;
  if (!nextAnalyticsAllowed && !previousAnalyticsAllowed) return;

  captureProductEvent("consent_updated", {
    version: nextConsent.version,
    categories: nextConsent.categories,
    previous_categories: previousConsent?.categories ?? null,
  });
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [consentData, setConsentData] = useState<ConsentData | null>(() => readConsentData());
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const hasConsent = Boolean(consentData);
  const analyticsAllowed = consentData?.categories.analytics === true;
  const monitoringAllowed = consentData?.categories.monitoring === true;
  const marketingAllowed = consentData?.categories.marketing === true;

  useLayoutEffect(() => {
    configureProductAnalyticsConsent(analyticsAllowed);
    configureClientMonitoringConsent({
      monitoringAllowed,
      analyticsAllowed,
    });
    syncMarketingAttribution({
      analyticsAllowed,
      marketingAllowed,
    });
  }, [analyticsAllowed, marketingAllowed, monitoringAllowed]);

  const persistConsent = useCallback((categories: Partial<ConsentCategories>) => {
    const previousConsent = readConsentData();
    const previousAnalyticsAllowed = previousConsent?.categories.analytics === true;
    const nextConsent = writeConsentData(categories);
    const nextAnalyticsAllowed = nextConsent.categories.analytics === true;

    if (nextAnalyticsAllowed && !previousAnalyticsAllowed) {
      configureProductAnalyticsConsent(true);
    }

    captureConsentUpdate(nextConsent, previousConsent, nextAnalyticsAllowed);

    if (!nextAnalyticsAllowed && previousAnalyticsAllowed) {
      configureProductAnalyticsConsent(false);
    }

    window.dispatchEvent(
      new CustomEvent("qc:consent-updated", {
        detail: {
          previousConsent,
          nextConsent,
        },
      }),
    );

    setConsentData(nextConsent);
    setPreferencesOpen(false);
  }, []);

  const acceptAll = useCallback(() => {
    persistConsent(buildAllConsentCategories());
  }, [persistConsent]);

  const rejectAll = useCallback(() => {
    persistConsent(buildEssentialConsentCategories());
  }, [persistConsent]);

  const savePreferences = useCallback(
    (categories: Partial<ConsentCategories>) => {
      persistConsent(categories);
    },
    [persistConsent],
  );

  const openPreferences = useCallback(() => {
    setPreferencesOpen(true);
  }, []);

  const isAllowed = useCallback(
    (category: ConsentCategory) => {
      if (category === "essential") return true;
      return consentData?.categories[category] === true;
    },
    [consentData],
  );

  const contextValue = useMemo<ConsentContextValue>(
    () => ({
      hasConsent,
      consentData,
      isAllowed,
      acceptAll,
      rejectAll,
      savePreferences,
      openPreferences,
    }),
    [acceptAll, consentData, hasConsent, isAllowed, openPreferences, rejectAll, savePreferences],
  );

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
      {!hasConsent ? (
        <ConsentBanner
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onOpenPreferences={openPreferences}
        />
      ) : null}
      <ConsentModal
        open={preferencesOpen}
        consentData={consentData}
        onOpenChange={setPreferencesOpen}
        onAcceptAll={acceptAll}
        onRejectAll={rejectAll}
        onSave={savePreferences}
      />
    </ConsentContext.Provider>
  );
}
